/**
 * ClueCell Component
 *
 * Displays a single cell in the puzzle grid.
 * Unified animation for both hint rows (0-3) and guess rows (4-5).
 *
 * All animations use a simple slide: the current button slides down
 * through a clip container, revealing the content already behind it.
 *
 * Guess buttons shrink and strobe while the dialog is open,
 * then spring back and slide out when the player submits or presses Back.
 *
 * Animation layers (back to front):
 *   0. ClueLozenge (base)        — ALWAYS in flex flow, stable background rect
 *   1. Behind content             — next button for guess/lockout transitions
 *   5. Clip container             — fixed size, overflow toggles, contains current button
 */

import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useGameContext } from '../../contexts/GameContext';
import HintButton from '../../svg/HintButton';
import GuessButton from '../../svg/GuessButton';
import OneMoreGuessButton from '../../svg/OneMoreGuessButton';
import AccessDeniedButton from '../../svg/AccessDeniedButton';
import ClueLozenge from './ClueLozenge';
import { signalPrismComplete, registerCreditAnimComplete } from '../../hooks/useCreditAnimation';
import {
  useHintRevealAnimation,
  DROP_DURATION,
  FULL_WIDTH,
  FULL_HEIGHT,
  SPRING_DURATION,
  HINT_PRESS_SCALE,
  HINT_PRESS_DURATION,
  HINT_PRESSED_WIDTH,
  HINT_BUTTON_DROP_DURATION,
  HINT_BUTTON_DROP_DELAY,
  LOZENGE_DROP_DURATION,
} from '../../hooks/useHintRevealAnimation';
import PrismSlot, { PrismFace } from '../PrismSlot';
import { createPrismFromSlot } from '../../utils/prismGeometry';
import styles from './ClueCell.module.css';

const HINT_PRESSED_HEIGHT = Math.round(FULL_HEIGHT * HINT_PRESS_SCALE);

// Prism geometry — hint (196×66) and outcome (208×78), face = slot
const HINT = createPrismFromSlot('horiz', FULL_HEIGHT, FULL_WIDTH);
const PRISM_ROTATION_DURATION = 504; // 840 * 0.6
// Guess button pressed state
const GUESS_PRISM_PRESS_DURATION = 280;
const GUESS_PRISM_BACK_DURATION = 280;
const GUESS_PRESSED_WIDTH = 184; // button shrinks from FULL_WIDTH to this

// Outcome reveal timing and highlight colors
import { COLORS } from '../../utils/constants';
const OUTCOME_GOLD = COLORS.highlight;
const OUTCOME_RED = '#b84927';      // theme red-orange — wrong answer flash
const OUTCOME_PRISM_DURATION = PRISM_ROTATION_DURATION;
const FLASH_ON_MS = 120;            // flash visible duration
const FLASH_GAP_MS = 100;           // gap between flashes
const FLASH_TAIL_MS = 80;           // pause after second flash before prism rotates
const OUTCOME_FACE_W = 208;
const OUTCOME_FACE_H = 78;
const OUTCOME = createPrismFromSlot('horiz', OUTCOME_FACE_H, OUTCOME_FACE_W);

type GuessOutcomePhase = 'idle' | 'reveal-mount' | 'flashing' | 'revealing';

// Module-level: hint cells register callbacks, fired when outcome prism completes
const pendingHintReveals = new Map<number, (() => void)[]>();

function registerPendingHintReveal(columnIndex: number, callback: () => void) {
  const arr = pendingHintReveals.get(columnIndex) || [];
  arr.push(callback);
  pendingHintReveals.set(columnIndex, arr);
}

function firePendingHintReveals(columnIndex: number) {
  const arr = pendingHintReveals.get(columnIndex);
  if (arr) {
    pendingHintReveals.delete(columnIndex);
    arr.forEach(fn => fn());
  }
}

// Module-level: solve-complete callback for Board celebration timing
// Board registers a callback; it fires after outcome prism + all hint prisms finish
let solveCompleteCallback: (() => void) | null = null;
let pendingSolveHintCount = 0;

export function registerSolveComplete(cb: (() => void) | null) {
  solveCompleteCallback = cb;
  pendingSolveHintCount = 0;
}

function fireSolveComplete() {
  if (solveCompleteCallback) {
    const cb = solveCompleteCallback;
    solveCompleteCallback = null;
    pendingSolveHintCount = 0;
    cb();
  }
}

// Module-level: lockout completion — Board registers callback, cells signal start/finish
let lockoutCompleteCallback: (() => void) | null = null;
let pendingLockoutCount = 0;

export function registerLockoutComplete(cb: (() => void) | null) {
  lockoutCompleteCallback = cb;
  if (cb === null) pendingLockoutCount = 0;
}

function lockoutCellStarted() {
  pendingLockoutCount++;
}

function lockoutCellFinished() {
  pendingLockoutCount--;
  if (pendingLockoutCount <= 0 && lockoutCompleteCallback) {
    const cb = lockoutCompleteCallback;
    lockoutCompleteCallback = null;
    pendingLockoutCount = 0;
    cb();
  }
}

type GuessPrismPhase = 'idle' | 'mount-press' | 'pressing' | 'pressed' | 'mount-return' | 'returning';
type GuessPrismFace1 = 'button' | 'clue' | 'one-more-guess' | 'access-denied';

type DropInContent = 'clue' | 'one-more-guess' | 'access-denied' | null;

interface ClueCellProps {
  columnIndex: number;
  rowIndex: number;
  celebrationGoldCol?: number;
  bannerDialogVisible?: boolean;
}

const ClueCell: React.FC<ClueCellProps> = ({ columnIndex, rowIndex, celebrationGoldCol = -1, bannerDialogVisible = false }) => {
  const {
    puzzle, credits, revealedClues, revealHint, openDialog, closeDialog,
    guessAttempts, solvedColumns, activeDialog, activeDialogColumn, activeDialogRow,
    lockoutAnimating,
  } = useGameContext();
  const anim = useHintRevealAnimation();

  // What content appears behind the button during the slide-out animation
  const [dropInContent, setDropInContent] = useState<DropInContent>(null);

  // Prism rotation for hint reveals (experimental)
  const [prismPhase, setPrismPhase] = useState<'idle' | 'mounted' | 'rotating'>('idle');

  // Prism rotation for guess buttons
  const [guessPrismPhase, setGuessPrismPhase] = useState<GuessPrismPhase>('idle');
  const [guessPrismFace1, setGuessPrismFace1] = useState<GuessPrismFace1>('button');
  const guessPrismReturnDurationRef = useRef(PRISM_ROTATION_DURATION);

  // Guess outcome color change + prism reveal
  const [guessOutcomePhase, setGuessOutcomePhase] = useState<GuessOutcomePhase>('idle');
  const [guessOutcomeCorrect, setGuessOutcomeCorrect] = useState(false);
  // Double-flash: 0=off, 1=first on, 2=first off, 3=second on, 4=done
  const [flashStep, setFlashStep] = useState(0);

  // Hint cell waiting for outcome prism to finish before starting its own prism
  const [awaitingSolveReveal, setAwaitingSolveReveal] = useState(false);

  // Persist gold background on guess cell during banner close (Back/click-to-close only)
  const [persistGold, setPersistGold] = useState(false);


  // Refs for detecting guess dialog outcome
  const preDialogAttemptsRef = useRef(0);
  const preDialogSolvedRef = useRef(false);
  const preDialogCreditsRef = useRef(0);

  // Snapshot of attempts at animation start — keeps the original button stable during slide-out
  const frozenAttemptsRef = useRef(0);

  // Track whether current animation is a lockout (slide-out) vs drop-in
  const isLockoutAnimRef = useRef(false);

  // Track whether current hint prism rotation is part of a column-solve sequence
  const isSolveRevealRef = useRef(false);

  // Keep button depressed during pre-pushing setup frame (only for guess outcomes)
  const keepDepressedRef = useRef(false);

  const clue = puzzle?.grid[columnIndex]?.[rowIndex];
  const isRevealed = revealedClues[columnIndex]?.[rowIndex] ?? false;
  const isGuessRow = rowIndex === 4 || rowIndex === 5;
  const isSolved = solvedColumns.includes(columnIndex);
  const attempts = guessAttempts[columnIndex] || 0;

  // Track previous state for column-solve animation (detect transition)
  const prevSolvedRef = useRef(isSolved);
  const prevRevealedRef = useRef(isRevealed);

  // --- Click handlers ---

  const handleHintClick = useCallback(() => {
    // Hints blocked at 1 credit — purchase would end the game
    if (credits > 1) {
      revealHint(columnIndex, rowIndex);
      setPrismPhase('mounted');
    }
  }, [credits, revealHint, columnIndex, rowIndex]);

  const handleGuessClick = useCallback(() => {
    // Click-to-close: if dialog is already open for this cell, close it
    if (activeDialog === 'column-guess' && activeDialogColumn === columnIndex && activeDialogRow === rowIndex) {
      closeDialog();
      return;
    }
    const att = guessAttempts[columnIndex] || 0;
    if (credits > 0 && att < 2) {
      isLockoutAnimRef.current = false;
      // Snapshot state before dialog opens
      preDialogAttemptsRef.current = att;
      preDialogSolvedRef.current = solvedColumns.includes(columnIndex);
      preDialogCreditsRef.current = credits;
      frozenAttemptsRef.current = att;
      setDropInContent(null);
      openDialog('column-guess', columnIndex, rowIndex);
    }
  }, [credits, guessAttempts, columnIndex, solvedColumns, openDialog, rowIndex, activeDialog, activeDialogColumn, activeDialogRow, closeDialog]);

  // Gray face click — same as dialog Back button
  const handleGrayFaceClick = useCallback(() => {
    closeDialog();
  }, [closeDialog]);

  // --- Detect guess dialog outcome (prism-based) ---

  useEffect(() => {
    if (!isGuessRow || guessPrismPhase !== 'pressed') return;
    // Dialog still open?
    if (activeDialog !== 'none' || activeDialogColumn !== null) return;

    const wasSolved = preDialogSolvedRef.current;
    const nowSolved = solvedColumns.includes(columnIndex);
    const prevAttempts = preDialogAttemptsRef.current;
    const nowAttempts = guessAttempts[columnIndex] || 0;

    if (nowSolved && !wasSolved) {
      // Column just solved — either correct guess or forced reveal (2nd wrong)
      setGuessPrismFace1('clue');
      guessPrismReturnDurationRef.current = PRISM_ROTATION_DURATION;
      setGuessPrismPhase('mount-return');
    } else if (nowAttempts > prevAttempts) {
      // Incorrect guess (1st wrong — 2nd wrong solves the column, handled above)
      setGuessPrismFace1('one-more-guess');
      guessPrismReturnDurationRef.current = PRISM_ROTATION_DURATION;
      setGuessPrismPhase('mount-return');
    } else {
      // Back button — prepare face/duration but stay 'pressed' until banner closes
      // (new effect below triggers 'mount-return' when bannerDialogVisible goes false)
      setGuessPrismFace1('button');
      guessPrismReturnDurationRef.current = GUESS_PRISM_BACK_DURATION;
    }
  }, [activeDialog, activeDialogColumn, guessPrismPhase, solvedColumns, guessAttempts, credits, columnIndex, isGuessRow]);

  // Un-press guess button once banner finishes closing (Back button case)
  useEffect(() => {
    if (!bannerDialogVisible && guessPrismPhase === 'pressed' && activeDialog === 'none' && activeDialogColumn === null) {
      setGuessPrismPhase('mount-return');
    }
  }, [bannerDialogVisible, guessPrismPhase, activeDialog, activeDialogColumn]);

  // All animations are simple slides — skip post-drop/grow phases
  useEffect(() => {
    if (anim.phase === 'post-drop' || anim.phase === 'pre-growing' || anim.phase === 'growing') {
      anim.finish();
    }
  }, [anim.phase, anim.finish]);

  // --- Detect lockout (credits hit zero) ---

  useEffect(() => {
    if (!lockoutAnimating) return;
    if (anim.isAnimating) return; // this cell is already animating
    if (guessPrismPhase !== 'idle') return; // prism is handling this cell
    if (isRevealed) return; // clue text already showing, no button to replace
    if (isSolved) return; // column already solved
    if (isGuessRow) return; // AccessDenied only applies to hint buttons
    if (isGuessRow && attempts >= 2) return; // already showing AccessDenied

    // This cell currently has a visible button that needs to animate to AccessDenied
    isLockoutAnimRef.current = true;
    keepDepressedRef.current = false;
    frozenAttemptsRef.current = attempts;
    setDropInContent('access-denied');
    lockoutCellStarted();
    anim.startDrop();
  }, [lockoutAnimating, anim.isAnimating, guessPrismPhase, isRevealed, isSolved, isGuessRow, attempts, anim.startDrop]);

  // --- Prism mount → rotate (double-rAF for CSS transition) ---

  useEffect(() => {
    if (prismPhase === 'mounted') {
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setPrismPhase('rotating');
        });
      });
      return () => { cancelled = true; };
    }
  }, [prismPhase]);

  // --- Guess prism mount phases (double-rAF for CSS transition) ---

  useEffect(() => {
    if (guessPrismPhase === 'mount-press' || guessPrismPhase === 'mount-return') {
      const nextPhase: GuessPrismPhase = guessPrismPhase === 'mount-press' ? 'pressing' : 'returning';
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setGuessPrismPhase(nextPhase);
        });
      });
      return () => { cancelled = true; };
    }
  }, [guessPrismPhase]);

  // --- Column-solve reveal: chain hint prism rotations after outcome prism ---

  useEffect(() => {
    if (!isGuessRow && isSolved && !prevSolvedRef.current && !prevRevealedRef.current && !anim.isAnimating) {
      let cancelled = false;
      setAwaitingSolveReveal(true);
      registerPendingHintReveal(columnIndex, () => {
        if (!cancelled) {
          isSolveRevealRef.current = true;
          setPrismPhase('mounted');
          setAwaitingSolveReveal(false);
        }
      });
      return () => { cancelled = true; };
    }
  }, [isSolved, isGuessRow, anim.isAnimating, columnIndex]);

  // --- Detect guess dialog close → flash or signal credit animation ---
  // useLayoutEffect (not useEffect) fires synchronously before paint, so setPersistGold(true)
  // takes effect in the same frame as activeGuessGold becoming false — no 1-frame flicker.

  const wasGuessDialogOpenRef = useRef(false);
  useLayoutEffect(() => {
    const isActiveGuess = isGuessRow && activeDialog === 'column-guess' && activeDialogColumn === columnIndex && activeDialogRow === rowIndex;
    if (wasGuessDialogOpenRef.current && !isActiveGuess) {
      // Dialog just closed — detect outcome
      const wasSolved = preDialogSolvedRef.current;
      const nowSolved = solvedColumns.includes(columnIndex);
      const prevAtt = preDialogAttemptsRef.current;
      const nowAtt = guessAttempts[columnIndex] || 0;

      if ((nowSolved && !wasSolved) || nowAtt > prevAtt) {
        // Correct guess: column solved AND credits unchanged (no penalty).
        // Force-reveal (wrong guess that solves): column solved BUT credits decreased.
        const creditsUnchanged = credits === preDialogCreditsRef.current;
        const isCorrect = nowSolved && !wasSolved && creditsUnchanged;
        setGuessOutcomeCorrect(isCorrect);
        setGuessOutcomePhase('reveal-mount');
        // Wrong guess: signal credit animation (red flash is handled by outcome prism only)
        if (!isCorrect) {
          registerCreditAnimComplete(() => {});
        }
      } else {
        // Back button — persist gold bg during banner close, signal credit anim
        setPersistGold(true);
        signalPrismComplete();
      }
    }
    wasGuessDialogOpenRef.current = isActiveGuess;
  }, [isGuessRow, activeDialog, activeDialogColumn, activeDialogRow, columnIndex, rowIndex, solvedColumns, guessAttempts]);

  // Clear persisted gold when banner finishes closing (prop-driven)
  useEffect(() => {
    if (!bannerDialogVisible) {
      setPersistGold(false);
    }
  }, [bannerDialogVisible]);

  // --- Outcome prism mount → flash → rotate ---
  // reveal-mount: prism mounts at 0° (double-rAF for layout), then → flashing
  // flashing: flashStep drives two on/off blinks, then → revealing
  // revealing: prism rotates to show outcome face

  useEffect(() => {
    if (guessOutcomePhase === 'reveal-mount') {
      let cancelled = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) {
            setFlashStep(1);
            setGuessOutcomePhase('flashing');
          }
        });
      });
      return () => { cancelled = true; };
    }
  }, [guessOutcomePhase]);

  useEffect(() => {
    if (guessOutcomePhase !== 'flashing') return;
    if (flashStep === 1 || flashStep === 3) {
      // Flash ON → schedule OFF
      const timer = setTimeout(() => setFlashStep(s => s + 1), FLASH_ON_MS);
      return () => clearTimeout(timer);
    }
    if (flashStep === 2) {
      // Gap between flashes → second flash ON
      const timer = setTimeout(() => setFlashStep(3), FLASH_GAP_MS);
      return () => clearTimeout(timer);
    }
    if (flashStep === 4) {
      // Flash done → pause, then advance to revealing
      let cancelled = false;
      const timer = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!cancelled) {
              setFlashStep(0);
              setGuessOutcomePhase('revealing');
            }
          });
        });
      }, FLASH_TAIL_MS);
      return () => { cancelled = true; clearTimeout(timer); };
    }
  }, [guessOutcomePhase, flashStep]);

  // Update previous-state refs (must run after solve detection effect)
  useEffect(() => {
    prevSolvedRef.current = isSolved;
    prevRevealedRef.current = isRevealed;
  }, [isSolved, isRevealed]);

  // --- Button gravity-drop state (hint and guess buttons) ---
  const isDropIn = dropInContent !== null && !isLockoutAnimRef.current;
  const [buttonDropOut, setButtonDropOut] = useState(false);

  useEffect(() => {
    if (anim.phase === 'dropping' && isDropIn) {
      const timer = setTimeout(() => setButtonDropOut(true), HINT_BUTTON_DROP_DELAY);
      return () => clearTimeout(timer);
    } else {
      setButtonDropOut(false);
    }
  }, [anim.phase, isDropIn]);

  // --- Early return for missing data ---

  if (!puzzle || !clue) {
    return <div className={styles.cell}>...</div>;
  }

  // --- Phase flags ---

  const { phase } = anim;
  const isPre = phase === 'pre-pushing';
  const isPushing = phase === 'pushing';
  const isPreDrop = phase === 'pre-dropping';
  const isDrop = phase === 'dropping';
  const isWaiting = phase === 'pushed-waiting';
  const isUnPushing = phase === 'un-pushing';
  const isAnimating = anim.isAnimating;

  // --- Base ClueLozenge (Layer 0) ---

  // Text shows when revealed/solved — suppressed during drop-in and prism rotation
  const baseText = (isRevealed || isSolved) && !(isDropIn && isAnimating) && prismPhase === 'idle' && guessPrismPhase === 'idle' && guessOutcomePhase === 'idle' ? clue.text : '';
  const showGreenBase = isGuessRow && clue.isHidden && isSolved && !isAnimating && guessPrismPhase === 'idle' && guessOutcomePhase === 'idle';
  const showGreenDropIn = isGuessRow && clue.isHidden && isSolved;

  // Celebration gold text: blink individual columns (0-3) or all-gold unison (5)
  // States: 0-3=individual, 4=pause(off), 5=all-gold, 6=done(reverted)
  const celebrationGold = showGreenBase && (
    celebrationGoldCol === columnIndex || celebrationGoldCol === 5
  );

  // Gold background for active guess cell while dialog is open
  const activeGuessGold = isGuessRow && activeDialog === 'column-guess' && activeDialogColumn === columnIndex && activeDialogRow === rowIndex;

  // --- Layer visibility ---

  // Behind content: only for lockout slide-out (AccessDenied behind sliding button)
  const showBehindContent = isAnimating && isLockoutAnimRef.current && dropInContent !== null;

  // Keep clip visible during lockout initiation (credits already 0 but animation not yet started)
  // Lockout only applies to hint buttons, not guess rows
  const pendingLockout = lockoutAnimating && !isAnimating && !isRevealed && !isSolved && !isGuessRow;

  // Prevent one-frame gap: keep clip visible when column just solved (before animation starts)
  const needsSolveAnim = !isGuessRow && isSolved && !prevSolvedRef.current && !isAnimating && !prevRevealedRef.current;

  // Clip container: visible when there's an idle button OR during animation phases
  const hasIdleButton = isGuessRow
    ? (!isSolved && !isRevealed && credits > 0 && attempts < 2)
    : (!isSolved && !isRevealed && credits > 1);
  const showClip = (hasIdleButton || isPre || isPushing || isPreDrop || isDrop || isWaiting || isUnPushing || pendingLockout || needsSolveAnim || awaitingSolveReveal)
    && guessPrismPhase === 'idle'
    && guessOutcomePhase === 'idle';

  // AccessDenied static overlay — ONLY for hint buttons when credits <= 1
  const showAccessDenied = !isAnimating && !pendingLockout && guessPrismPhase === 'idle' && guessOutcomePhase === 'idle' && !isSolved && !isRevealed && (
    !isGuessRow && (credits === 0 || credits === 1)
  );

  // --- Animation values ---

  // Button stays depressed during drop phase and pre-push setup
  const isDepressed = isWaiting || isPushing || isPreDrop || (isDropIn && isDrop)
    || (keepDepressedRef.current && isPre);

  // Block clicks on all buttons when a dialog is open
  const isDialogOpen = activeDialog !== 'none';
  const isLocked = (isDialogOpen || guessOutcomePhase !== 'idle') && !isDepressed && guessPrismPhase === 'idle' && !activeGuessGold;

  const buttonScale = isDepressed ? HINT_PRESS_SCALE : 1;
  const scaleTransition = (isWaiting || isPushing)
    ? `transform ${HINT_PRESS_DURATION}ms ease`
    : isUnPushing
      ? `transform ${SPRING_DURATION}ms ease-out`
      : 'transform 200ms ease';
  const showBacking = false;

  // Button drops out the bottom (hint/guess); lockout buttons slide up
  const buttonDroppingOut = isDropIn && buttonDropOut;
  const dropOffset = (isDrop && !isDropIn) ? -FULL_HEIGHT
    : buttonDroppingOut ? FULL_HEIGHT
    : 0;
  const clipOverflow = (isDrop || isPreDrop || isWaiting || isPre || isPushing || isUnPushing) ? 'hidden' as const : 'visible' as const;

  // Drop-in: clip shrinks to pressed-button size during pre-drop and drop
  const dropClipActive = isDropIn && (isPreDrop || isDrop);
  const clipW = dropClipActive ? HINT_PRESSED_WIDTH : FULL_WIDTH;
  const clipH = dropClipActive ? HINT_PRESSED_HEIGHT : FULL_HEIGHT;

  // --- Determine button for clip container ---
  // Use frozen attempts during animation so the original button stays visible while sliding out

  const displayAttempts = (isGuessRow && (isAnimating || guessOutcomePhase !== 'idle')) ? frozenAttemptsRef.current : attempts;

  let ClipButton: React.FC<{ onClick?: () => void; style?: React.CSSProperties }>;
  let clipButtonClick: (() => void) | undefined;

  if (isGuessRow) {
    if (displayAttempts === 0) {
      ClipButton = GuessButton;
      clipButtonClick = handleGuessClick;
    } else {
      ClipButton = OneMoreGuessButton;
      clipButtonClick = handleGuessClick;
    }
  } else {
    ClipButton = HintButton;
    clipButtonClick = handleHintClick;
  }

  // --- Render behind content (lockout slide-out only) ---

  const renderBehindContent = () => {
    switch (dropInContent) {
      case 'access-denied':
        return <AccessDeniedButton style={{ width: '100%', height: '100%' }} />;
      default:
        return null;
    }
  };

  // --- Render drop-in content (drops from above for hint/guess reveals) ---

  const renderDropIn = () => {
    switch (dropInContent) {
      case 'clue':
        return <ClueLozenge text={clue.text} rowIndex={rowIndex} rounded showGreenGuessBackground={showGreenDropIn} />;
      case 'one-more-guess':
        return <OneMoreGuessButton />;
      case 'access-denied':
        return <AccessDeniedButton />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.cell}>
      {/* DEBUG: hidden click target for automated test script */}
      {isGuessRow && rowIndex === 5 && puzzle && (
        <div
          data-debug-guess-col={columnIndex}
          data-debug-answer={puzzle.grid[columnIndex]?.[5]?.text ?? ''}
          style={{ display: 'none' }}
          onClick={handleGuessClick}
        />
      )}

      {/* Layer 0: Base ClueLozenge — ALWAYS present, stable background */}
      <ClueLozenge text={baseText} rowIndex={rowIndex} showGreenGuessBackground={showGreenBase} textColorOverride={celebrationGold ? OUTCOME_GOLD : undefined} bgColorOverride={
        (activeGuessGold || (persistGold && bannerDialogVisible)) ? OUTCOME_GOLD
        : undefined
      } />

      {/* Layer 1: Behind content — next button visible during slide-out */}
      {showBehindContent && (
        <div
          key="behind"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: FULL_WIDTH,
            height: FULL_HEIGHT,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          {renderBehindContent()}
        </div>
      )}

      {/* Layer 5: Clip container — full button size, centered in cell */}
      {showClip && (
        <div
          key="clip"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: clipW,
            height: clipH,
            borderRadius: 12,
            overflow: clipOverflow,
            zIndex: 5,
          }}
        >
          {/* Brown-gray backing — visible through button shrink gap */}
          {showBacking && (
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 12,
              backgroundColor: '#6b5f54',
            }} />
          )}

          {/* Drop wrapper — slides button down through clip area */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: `translateY(${dropOffset}px)`,
              transition: (isDrop && !isDropIn)
                ? `transform ${DROP_DURATION}ms ease`
                : buttonDroppingOut
                  ? `transform ${HINT_BUTTON_DROP_DURATION}ms ease-in`
                  : 'none',
            }}
            onTransitionEnd={(e) => {
              if (e.target === e.currentTarget && e.propertyName === 'transform') {
                // Drop-in: button drops out but lozenge handles onDropEnd
                if (!isDropIn) {
                  anim.onDropEnd();
                  if (isLockoutAnimRef.current) lockoutCellFinished();
                }
              }
            }}
          >
            {/* Button wrapper — scale transition for pressed state */}
            <div
              style={{
                width: FULL_WIDTH,
                height: FULL_HEIGHT,
                transform: `translate(-50%, -50%) scale(${buttonScale})`,
                filter: isDepressed ? 'brightness(0.7)' : 'brightness(1)',
                transition: `${scaleTransition}, filter 200ms ease`,
                borderRadius: 12,
                overflow: 'hidden',
              }}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                  if (phase === 'pushing') anim.onPushEnd();
                  if (phase === 'un-pushing') anim.onUnPushEnd();
                }
              }}
            >
              <ClipButton
                onClick={(!isAnimating && !isDialogOpen && guessOutcomePhase === 'idle')
                  || (activeGuessGold && isDialogOpen) ? clipButtonClick : undefined}
                style={{ width: '100%', height: '100%', cursor: isLocked ? 'not-allowed' : undefined }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Layer 6: Drop-in content — drops down from above during hint/guess reveal */}
      {isDropIn && (isPushing || isPre || isPreDrop || isDrop) && (
        <div
          key="drop-in"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: FULL_WIDTH,
            height: FULL_HEIGHT,
            borderRadius: 12,
            overflow: 'hidden',
            zIndex: 6,
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              transform: `translateY(${isDrop ? 0 : -FULL_HEIGHT}px)`,
              transition: isDrop ? `transform ${LOZENGE_DROP_DURATION}ms ease` : 'none',
            }}
            onTransitionEnd={(e) => {
              if (e.target === e.currentTarget && e.propertyName === 'transform') {
                anim.onDropEnd();
              }
            }}
          >
            {renderDropIn()}
          </div>
        </div>
      )}

      {/* Layer 7: Prism — fold rotation for hint reveal */}
      {prismPhase !== 'idle' && !isGuessRow && (
        <PrismSlot
          geometry={HINT}
          angle={prismPhase === 'rotating' ? 120 : 0}
          transition={prismPhase === 'rotating'
            ? `transform ${PRISM_ROTATION_DURATION}ms ease-in-out` : undefined}
          onTransitionEnd={() => {
            setPrismPhase('idle');
            signalPrismComplete();
            if (isSolveRevealRef.current) {
              isSolveRevealRef.current = false;
              pendingSolveHintCount--;
              console.log(`[DOOR-DEBUG] hint prism done, remaining=${pendingSolveHintCount}`);
              if (pendingSolveHintCount <= 0) {
                console.log('[DOOR-DEBUG] All hints done → fireSolveComplete()');
                fireSolveComplete();
              }
            }
          }}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 7,
            pointerEvents: 'none',
          }}
        >
          {/* Face 1: Button on cell background (front face, rotates away) */}
          <PrismFace angle={0} style={{ backgroundColor: rowIndex % 2 === 0 ? '#f7e5c5' : '#262626' }}>
            {credits <= 1
              ? <AccessDeniedButton style={{ width: '100%', height: '100%' }} />
              : <HintButton style={{ width: '100%', height: '100%' }} />}
          </PrismFace>
          {/* Face 2: clue text on cell background */}
          <PrismFace angle={-120} style={{
            backgroundColor: rowIndex % 2 === 0 ? '#f7e5c5' : '#262626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <ClueLozenge text={clue.text} rowIndex={rowIndex} />
          </PrismFace>
        </PrismSlot>
      )}

      {/* Layer 8: Guess button pressed state — brown backing rect + shrunk button */}
      {guessPrismPhase !== 'idle' && isGuessRow && (() => {
        let scale: number;
        let scaleTransition: string;

        switch (guessPrismPhase) {
          case 'mount-press':
            scale = 1;
            scaleTransition = 'none';
            break;
          case 'pressing':
            scale = GUESS_PRESSED_WIDTH / FULL_WIDTH;
            scaleTransition = `transform ${GUESS_PRISM_PRESS_DURATION}ms ease`;
            break;
          case 'pressed':
          case 'mount-return':
            scale = GUESS_PRESSED_WIDTH / FULL_WIDTH;
            scaleTransition = 'none';
            break;
          case 'returning':
            scale = 1;
            scaleTransition = `transform ${guessPrismReturnDurationRef.current}ms ease-out`;
            break;
          default:
            scale = 1;
            scaleTransition = 'none';
        }

        // Content: button at rest, swapped to reveal content during return
        const face1 = (() => {
          switch (guessPrismFace1) {
            case 'clue':
              return <ClueLozenge text={clue.text} rowIndex={rowIndex} rounded showGreenGuessBackground={clue.isHidden} />;
            case 'one-more-guess':
              return <OneMoreGuessButton style={{ width: '100%', height: '100%' }} />;
            case 'access-denied':
              return <AccessDeniedButton style={{ width: '100%', height: '100%' }} />;
            default: {
              const att = frozenAttemptsRef.current;
              if (att === 0) return <GuessButton style={{ width: '100%', height: '100%' }} />;
              return <OneMoreGuessButton style={{ width: '100%', height: '100%' }} />;
            }
          }
        })();

        return (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: FULL_WIDTH,
              height: FULL_HEIGHT,
              zIndex: 8,
              pointerEvents: guessPrismPhase === 'pressed' ? 'auto' : 'none',
            }}
          >
            {/* Dark-gold backing rect — revealed when button shrinks */}
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 12,
              backgroundColor: '#b4871f',
            }} />
            {/* Button — scales down when pressed, pops back on return */}
            <div
              style={{
                width: FULL_WIDTH,
                height: FULL_HEIGHT,
                transform: `scale(${scale})`,
                transition: scaleTransition,
                borderRadius: 12,
                overflow: 'hidden',
                cursor: guessPrismPhase === 'pressed' ? 'pointer' : 'default',
              }}
              onClick={guessPrismPhase === 'pressed' ? handleGrayFaceClick : undefined}
              onTransitionEnd={(e) => {
                if (e.propertyName === 'transform' && e.target === e.currentTarget) {
                  if (guessPrismPhase === 'pressing') {
                    setGuessPrismPhase('pressed');
                  } else if (guessPrismPhase === 'returning') {
                    setGuessPrismFace1('button');
                    setGuessPrismPhase('idle');
                    signalPrismComplete();
                  }
                }
              }}
            >
              {face1}
            </div>
          </div>
        );
      })()}

      {/* Layer 9: Outcome prism — 208×78 slot, face = slot */}
      {(guessOutcomePhase === 'reveal-mount' || guessOutcomePhase === 'flashing' || guessOutcomePhase === 'revealing') && isGuessRow && (() => {
        const angle = guessOutcomePhase === 'revealing'
          ? (isSolved ? 120 : -120)
          : 0;

        // Double-flash: surround border flashes gold (correct) or red-orange (wrong)
        // Flash returns to normal bg before rotation starts
        const isFlashOn = flashStep === 1 || flashStep === 3;
        const normalBg = rowIndex % 2 === 0 ? '#f7e5c5' : '#262626';
        const flashBg = isFlashOn
          ? (guessOutcomeCorrect ? OUTCOME_GOLD : OUTCOME_RED)
          : normalBg;

        // Next button for incorrect outcome — always OneMoreGuessButton for guess rows
        const nextButton = <OneMoreGuessButton style={{ width: FULL_WIDTH, height: FULL_HEIGHT }} />;

        return (
          <PrismSlot
            geometry={OUTCOME}
            angle={angle}
            transition={guessOutcomePhase === 'revealing'
              ? `transform ${OUTCOME_PRISM_DURATION}ms ease-in-out` : undefined}
            onTransitionEnd={() => {
              setGuessOutcomePhase('idle');
              signalPrismComplete();
              // Fire hint reveals + celebration for any column solve (correct OR force-reveal)
              if (isSolved) {
                const hints = pendingHintReveals.get(columnIndex);
                pendingSolveHintCount = hints ? hints.length : 0;
                console.log(`[DOOR-DEBUG] outcome prism done col=${columnIndex}, pendingHints=${pendingSolveHintCount}, solveCallback=${!!solveCompleteCallback}`);
                firePendingHintReveals(columnIndex);
                if (pendingSolveHintCount === 0) {
                  console.log('[DOOR-DEBUG] No pending hints → fireSolveComplete()');
                  fireSolveComplete();
                }
              }
            }}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9,
              pointerEvents: 'none',
            }}
          >
            {/* Face 1: Current guess button — surround flashes gold/red before rotation */}
            <PrismFace angle={0} style={{
              backgroundColor: flashBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {frozenAttemptsRef.current === 0 && <GuessButton style={{ width: FULL_WIDTH, height: FULL_HEIGHT }} />}
              {frozenAttemptsRef.current >= 1 && <OneMoreGuessButton style={{ width: FULL_WIDTH, height: FULL_HEIGHT }} />}
            </PrismFace>

            {/* Face 2: solved reveal — backward rotation (column solved: correct OR force-reveal) */}
            {isSolved && (
              <PrismFace angle={-120} style={{
                backgroundColor: rowIndex % 2 === 0 ? '#f7e5c5' : '#262626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <ClueLozenge text={clue.text} rowIndex={rowIndex} rounded showGreenGuessBackground={clue.isHidden} />
              </PrismFace>
            )}

            {/* Face 3: incorrect reveal — forward rotation (1st wrong guess) */}
            {!isSolved && (
              <PrismFace angle={120} style={{
                backgroundColor: rowIndex % 2 === 0 ? '#f7e5c5' : '#262626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {nextButton}
              </PrismFace>
            )}
          </PrismSlot>
        );
      })()}

      {/* AccessDenied static — shown when credits=0 or attempts>=3, not animating */}
      {showAccessDenied && (
        <AccessDeniedButton key="access-denied" style={{ position: 'absolute' }} />
      )}
    </div>
  );
};

export default React.memo(ClueCell);
